import { useEffect, useState } from 'react';
import type { BotTier } from '@qoobd/rules';
import { game, PLAYER, RIVAL, WATCH_NAMES } from '../game/controller';
import { savePrefs, useGame, type SpectatorAgent, type WatchSettings } from '../game/store';
import { audio } from '../audio/engine';
import { ItemIcon } from './icons';
import { formatTime } from './Hud';

const TIER_NAME: Record<BotTier, string> = { novice: 'Novice', tactical: 'Tactical', relentless: 'Relentless' };
const TIERS: BotTier[] = ['novice', 'tactical', 'relentless'];
const THREAT: Record<string, string> = { none: 'clear', detected: 'detected', near: 'near', danger: 'danger close' };

function AgentCard({ id, a, tier, watched }: { id: string; a: SpectatorAgent | undefined; tier: BotTier; watched: boolean }) {
  return (
    <button
      type="button"
      className={`panel spec-agent ${id === PLAYER ? 'amber' : 'ice'}${watched ? ' watched' : ''}`}
      aria-pressed={watched}
      aria-label={`Follow ${WATCH_NAMES[id]}`}
      onClick={() => useGame.setState((s) => ({ watch: { ...s.watch, follow: id } }))}
    >
      <div className="spec-name">
        {WATCH_NAMES[id]} <span>{TIER_NAME[tier]}</span>
      </div>
      {a && (
        <>
          <div className="spec-item">
            <ItemIcon item={a.item} size={16} />
            {a.item.toUpperCase()}
          </div>
          <div className="spec-meta">
            <span className={a.threat !== 'none' ? 't-red' : ''}>Threat {a.jammed ? 'jammed' : THREAT[a.threat]}</span>
            <span className={a.core !== 'none' ? 't-cyan' : ''}>Core {a.jammed ? 'jammed' : a.core === 'none' ? 'no signal' : a.core}</span>
            <span>
              EMP {a.emp} · Seal {a.bulkhead} · {a.steps} steps
            </span>
          </div>
        </>
      )}
    </button>
  );
}

function Clock() {
  const startedAt = useGame((s) => s.startedAt);
  const speed = useGame((s) => s.watch.speed);
  const final = useGame((s) => (s.phase === 'over' ? (s.result?.elapsedMs ?? null) : null));
  const [now, setNow] = useState(0);
  useEffect(() => {
    if (final !== null) return;
    const id = setInterval(() => setNow(game.now()), 250);
    return () => clearInterval(id);
  }, [startedAt, speed, final]);
  return <span className="spec-clock">{formatTime(final ?? now)}</span>;
}

/** Spectator overlay: both agents' items and sensors, an intercept warning, a feed and camera controls. */
export function SpectatorHud() {
  const sp = useGame((s) => s.spectator);
  const w = useGame((s) => s.watch);
  const soundOn = useGame((s) => s.soundOn);
  const set = (patch: Partial<WatchSettings>) => useGame.setState((s) => ({ watch: { ...s.watch, ...patch } }));
  const [setup, setSetup] = useState(false);
  return (
    <>
      <div className="hud spec-top">
        <div className="spec-title">
          <span className="eyebrow">AI vs AI · live</span>
          <Clock />
        </div>
        <div className="spec-agents">
          <AgentCard id={PLAYER} a={sp?.agents[PLAYER]} tier={w.tiers[0]} watched={w.follow === PLAYER} />
          <AgentCard id={RIVAL} a={sp?.agents[RIVAL]} tier={w.tiers[1]} watched={w.follow === RIVAL} />
        </div>
        {sp?.intercept != null && (
          <div className="spec-intercept" role="status">
            {sp.intercept === 0 ? 'SAME ROOM' : `INTERCEPT IN ${sp.intercept}`}
          </div>
        )}
        {sp?.cloaked && <div className="spec-cloak">CLOAKED STANDOFF</div>}
      </div>

      <div className="hud spec-feed" aria-live="polite">
        {sp?.feed.map((f) => (
          <div key={f.id} className={f.side === PLAYER ? 'amber' : f.side === RIVAL ? 'ice' : ''}>
            {f.text}
          </div>
        ))}
      </div>

      <div className="hud spec-controls" role="toolbar" aria-label="Spectator camera">
        <button type="button" aria-pressed={w.zoom === 'in'} onClick={() => set({ zoom: w.zoom === 'in' ? 'out' : 'in' })}>
          {w.zoom === 'in' ? 'Zoom out' : 'Zoom in'}
        </button>
        <button type="button" aria-pressed={w.reorient} onClick={() => set({ reorient: !w.reorient })}>
          Auto-reorient {w.reorient ? 'on' : 'off'}
        </button>
        <button type="button" aria-pressed={w.trails} onClick={() => set({ trails: !w.trails })}>
          Trails {w.trails ? 'on' : 'off'}
        </button>
        <button
          type="button"
          onClick={() => {
            const speed = w.speed === 1 ? 2 : 1;
            game.setSpeed(speed);
            set({ speed });
          }}
        >
          Speed {w.speed}×
        </button>
        <button type="button" onClick={() => setSetup(!setup)} aria-expanded={setup}>
          Bots
        </button>
        <button
          type="button"
          onClick={() => {
            audio.setSound(!soundOn);
            useGame.setState({ soundOn: !soundOn });
            savePrefs();
          }}
        >
          Sound {soundOn ? 'on' : 'off'}
        </button>
        <button type="button" onClick={() => useGame.setState({ phase: 'intro', mode: 'play' })}>
          Title
        </button>
      </div>

      {setup && (
        <div className="hud panel spec-setup">
          {([0, 1] as const).map((seat) => (
            <div key={seat} className="spec-seat">
              <span className={seat === 0 ? 'amber' : 'ice'}>{seat === 0 ? 'AMBER' : 'ICE'}</span>
              <div className="tier" role="group" aria-label={`${seat === 0 ? 'Amber' : 'Ice'} difficulty`}>
                {TIERS.map((t) => (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={w.tiers[seat] === t}
                    onClick={() => {
                      const tiers: [BotTier, BotTier] = [...w.tiers];
                      tiers[seat] = t;
                      set({ tiers });
                    }}
                  >
                    {TIER_NAME[t]}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button
            type="button"
            className="cta"
            onClick={() => {
              setSetup(false);
              game.watch(useGame.getState().watch.tiers);
            }}
          >
            Start a new match
          </button>
        </div>
      )}
    </>
  );
}
